import { useState, useEffect, useCallback } from 'react';
import useUser from '@/hooks/useUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { GAME_POINTS, CEREBRIX_CONSTANTS, GAME_CONFIG } from './constants';
import { Card, MemoryGameState, CardCategory, Category, Level } from './types';
import { financialTerms, investmentTerms, savingsTerms, budgetTerms, creditTerms, taxTerms } from './data';
import { MEMORY_CONTENT } from './content';

// Estado inicial del juego
const initialState: MemoryGameState = {
  cards: [], // Inicializar como array vacío
  matchedPairs: 0,
  flippedCards: [],
  gameStarted: false,
  isGameOver: false,
  timeLeft: 60, // Cambiar a 60 segundos
  currentLevel: 'basic',
  currentCategory: 'financial',
  stats: {
    points: 0,
    errors: 0,
    currentStreak: 0,
    maxStreak: 0,
    startTime: 0,
    categoriesCompleted: []
  },
  cerebrixEarned: 0 // Agregar propiedad cerebrixEarned
};

export const useMemoryGame = () => {
  const [gameState, setGameState] = useState<MemoryGameState>(initialState);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { toast } = useToast(); // Agregar desestructuración de useToast

  // Obtener el ID del juego
  const { data: gameId, refetch: refetchGameId } = useQuery({
    queryKey: ['memory-game-id'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id')
        .eq('title', 'Memory Cards')
        .single();

      if (error) {
        console.error('Error obteniendo ID del juego:', error);
        return null;
      }

      console.log('ID del juego obtenido:', data?.id);
      return data?.id || null;
    },
    enabled: !!user?.id,
    staleTime: Infinity // Mantener el resultado en caché
  });

  // Generar cartas según la categoría
  const generateCards = (category: string) => {
    let cards: Card[] = [];
    
    switch (category) {
      case 'financial':
        cards = [
          { id: '1', term: 'Ahorro', definition: 'Guardar dinero para el futuro', emoji: '💰', category: 'financial' },
          { id: '2', term: 'Inversión', definition: 'Hacer crecer tu dinero', emoji: '📈', category: 'financial' },
          { id: '3', term: 'Presupuesto', definition: 'Plan para tus gastos', emoji: '📊', category: 'financial' },
          { id: '4', term: 'Deuda', definition: 'Dinero que debes pagar', emoji: '💳', category: 'financial' },
          { id: '5', term: 'Ganancia', definition: 'Dinero que obtienes', emoji: '💵', category: 'financial' },
          { id: '6', term: 'Impuesto', definition: 'Pago al gobierno', emoji: '📝', category: 'financial' }
        ];
        break;
      
      case 'business':
        cards = [
          { id: '1', term: 'Empresa', definition: 'Organización de negocios', emoji: '🏢', category: 'business' },
          { id: '2', term: 'Cliente', definition: 'Comprador de productos', emoji: '🤝', category: 'business' },
          { id: '3', term: 'Venta', definition: 'Intercambio por dinero', emoji: '💎', category: 'business' },
          { id: '4', term: 'Marketing', definition: 'Promoción del negocio', emoji: '📢', category: 'business' },
          { id: '5', term: 'Producto', definition: 'Lo que vendes', emoji: '📦', category: 'business' },
          { id: '6', term: 'Mercado', definition: 'Donde vendes', emoji: '🏪', category: 'business' }
        ];
        break;
      
      case 'leadership':
        cards = [
          { id: '1', term: 'Líder', definition: 'Guía del equipo', emoji: '👑', category: 'leadership' },
          { id: '2', term: 'Equipo', definition: 'Grupo de trabajo', emoji: '👥', category: 'leadership' },
          { id: '3', term: 'Meta', definition: 'Objetivo a lograr', emoji: '🎯', category: 'leadership' },
          { id: '4', term: 'Motivación', definition: 'Impulso para actuar', emoji: '⭐', category: 'leadership' },
          { id: '5', term: 'Decisión', definition: 'Elegir una opción', emoji: '🔄', category: 'leadership' },
          { id: '6', term: 'Éxito', definition: 'Logro alcanzado', emoji: '🏆', category: 'leadership' }
        ];
        break;
      
      case 'emotional':
        cards = [
          { id: '1', term: 'Alegría', definition: 'Sentirse feliz', emoji: '😊', category: 'emotional' },
          { id: '2', term: 'Confianza', definition: 'Creer en ti mismo', emoji: '💪', category: 'emotional' },
          { id: '3', term: 'Empatía', definition: 'Entender a otros', emoji: '🤗', category: 'emotional' },
          { id: '4', term: 'Paciencia', definition: 'Saber esperar', emoji: '🧘', category: 'emotional' },
          { id: '5', term: 'Gratitud', definition: 'Dar las gracias', emoji: '🙏', category: 'emotional' },
          { id: '6', term: 'Calma', definition: 'Estar tranquilo', emoji: '🌟', category: 'emotional' }
        ];
        break;
      
      default:
        cards = [];
    }

    // Duplicar las cartas para crear pares
    const allCards = [...cards, ...cards].map((card, index) => ({
      ...card,
      id: `${card.id}-${index}`,
      isFlipped: false,
      isMatched: false
    }));

    // Mezclar las cartas
    return allCards.sort(() => Math.random() - 0.5);
  };

  // Iniciar el juego
  const startGame = () => {
    const initialCards = generateCards(gameState.currentCategory);
    setGameState(prev => ({
      ...prev,
      cards: initialCards,
      gameStarted: true,
      timeLeft: 60, // Cambiar a 60 segundos
      matchedPairs: 0,
      flippedCards: [],
      isGameOver: false,
      stats: {
        ...prev.stats,
        points: 0,
        errors: 0,
        currentStreak: 0,
        maxStreak: 0,
        startTime: Date.now(),
        categoriesCompleted: []
      }
    }));
  };

  // Seleccionar categoría e iniciar juego
  const selectCategory = (category: Category) => {
    const initialCards = generateCards(category);
    setGameState(prev => ({
      ...prev,
      currentCategory: category,
      cards: initialCards,
      gameStarted: true,
      timeLeft: 60, // Cambiar a 60 segundos
      matchedPairs: 0,
      flippedCards: [],
      isGameOver: false,
      stats: {
        points: 0,
        errors: 0,
        currentStreak: 0,
        maxStreak: 0,
        startTime: Date.now(),
        categoriesCompleted: []
      }
    }));
  };

  // Manejar click en carta
  const handleCardClick = (index: number) => {
    if (
      !gameState.gameStarted ||
      gameState.isGameOver ||
      gameState.cards[index].isFlipped ||
      gameState.cards[index].isMatched ||
      gameState.flippedCards.length >= 2
    ) {
      return;
    }

    // Voltear la carta seleccionada
    const newCards = [...gameState.cards];
    newCards[index] = { ...newCards[index], isFlipped: true };

    // Agregar el índice a las cartas volteadas
    const newFlippedCards = [...gameState.flippedCards, index];

    setGameState(prev => ({
      ...prev,
      cards: newCards,
      flippedCards: newFlippedCards,
      lastFlipTime: Date.now()
    }));

    // Si hay dos cartas volteadas, verificar si son pares
    if (newFlippedCards.length === 2) {
      const [firstIndex, secondIndex] = newFlippedCards;
      const firstCard = newCards[firstIndex];
      const secondCard = newCards[secondIndex];
      const isMatch = firstCard.term === secondCard.term;

      if (isMatch) {
        // Es un par, marcarlas como encontradas
        setTimeout(() => {
          const updatedCards = [...newCards];
          updatedCards[firstIndex] = { ...updatedCards[firstIndex], isMatched: true };
          updatedCards[secondIndex] = { ...updatedCards[secondIndex], isMatched: true };

          const newMatchedPairs = gameState.matchedPairs + 1;
          const timeTaken = Date.now() - gameState.lastFlipTime;
          const isFastMatch = timeTaken < 3000; // menos de 3 segundos
          const newStreak = gameState.stats.currentStreak + 1;

          setGameState(prev => ({
            ...prev,
            cards: updatedCards,
            flippedCards: [],
            matchedPairs: newMatchedPairs,
            stats: {
              ...prev.stats,
              points: prev.stats.points + GAME_POINTS.MEMORY.MATCH_PAIR,
              fastMatches: isFastMatch ? prev.stats.fastMatches + 1 : prev.stats.fastMatches,
              currentStreak: newStreak,
              maxStreak: Math.max(prev.stats.maxStreak, newStreak)
            }
          }));

          // Si todos los pares están encontrados, terminar el juego
          if (newMatchedPairs === newCards.length / 2) {
            console.log('Todos los pares encontrados en handleCardClick...');
            setGameState(prev => ({
              ...prev,
              isGameOver: true,
              gameStarted: false
            }));
            setTimeout(() => endGame(), 0);
          }
        }, 500);
      } else {
        // No es un par, voltearlas de nuevo
        setTimeout(() => {
          setGameState(prev => ({
            ...prev,
            cards: prev.cards.map((card, i) => 
              i === firstIndex || i === secondIndex
                ? { ...card, isFlipped: false }
                : card
            ),
            flippedCards: [],
            stats: {
              ...prev.stats,
              errors: prev.stats.errors + 1,
              currentStreak: 0
            }
          }));
        }, 1000);
      }
    }
  };

  const calculateFinalScore = () => {
    const stats = {
      completionTime: 60 - gameState.timeLeft,
      totalTime: 60,
      fastMatches: gameState.stats.fastMatches,
      maxStreak: gameState.stats.maxStreak,
      errors: gameState.stats.errors
    };

    let points = 0;

    // Puntos base por cada par encontrado (10 puntos por par)
    points += (gameState.matchedPairs * GAME_POINTS.MEMORY.MATCH_PAIR);

    // Bonus por completar el nivel
    if (gameState.matchedPairs === gameState.cards.length / 2) {
      points += GAME_POINTS.MEMORY.COMPLETE_LEVEL;
    }

    // Bonus por tiempo perfecto (menos de 60 segundos)
    if (stats.completionTime <= 60) {
      points += GAME_POINTS.MEMORY.PERFECT_TIME;
    }

    // Bonus por no cometer errores
    if (gameState.stats.errors === 0) {
      points += GAME_POINTS.MEMORY.NO_MISTAKES;
    }

    return points;
  };

  // Finalizar el juego
  const endGame = async () => {
    console.log('Intentando finalizar juego...');
    
    // Verificar si el juego ya ha finalizado
    if (gameState.isGameOver) {
      console.log('El juego ya ha finalizado');
      return;
    }

    // Detener el juego inmediatamente para evitar múltiples llamadas
    setGameState(prev => ({
      ...prev,
      isGameOver: true,
      gameStarted: false
    }));

    // Calcular puntuación final
    const finalScore = calculateFinalScore();
    console.log('Puntuación final:', finalScore);

    // Calcular bonificaciones para mostrar
    const bonuses = [];
    const isComplete = gameState.matchedPairs === gameState.cards.length / 2;
    
    // Puntos base por pares
    bonuses.push({
      description: 'Pares encontrados',
      points: gameState.matchedPairs * GAME_POINTS.MEMORY.MATCH_PAIR,
      icon: '🎯'
    });

    // Bonus por completar
    if (isComplete) {
      bonuses.push({
        description: '¡Nivel completado!',
        points: GAME_POINTS.MEMORY.COMPLETE_LEVEL,
        icon: '🌟'
      });
    }

    // Bonus por tiempo
    if (60 - gameState.timeLeft <= 60) {
      bonuses.push({
        description: '¡Tiempo perfecto!',
        points: GAME_POINTS.MEMORY.PERFECT_TIME,
        icon: '⚡'
      });
    }

    // Bonus por no errores
    if (gameState.stats.errors === 0) {
      bonuses.push({
        description: '¡Sin errores!',
        points: GAME_POINTS.MEMORY.NO_MISTAKES,
        icon: '✨'
      });
    }

    // Mostrar mensaje inicial
    const message = isComplete
      ? '¡Felicitaciones! ¡Has encontrado todos los pares! 🎉'
      : '¡Se acabó el tiempo! ⏰';

    toast({
      title: message,
      description: 'Calculando tu puntuación...',
      duration: 2000,
    });

    // Intentar obtener la sesión y el gameId si no están disponibles
    if (!user?.id) {
      console.log('Sesión no disponible, mostrando solo puntuación local');
      
      // Mostrar resultados sin Cerebrix
      let resultsMessage = `¡Obtuviste ${finalScore} puntos!\n\n`;
      
      // Mostrar desglose de puntos
      bonuses.forEach(bonus => {
        resultsMessage += `${bonus.icon} ${bonus.description}: +${bonus.points}\n`;
      });
      
      toast({
        title: message,
        description: resultsMessage + "\n⚠️ No se pudieron guardar tus Cerebrix. Por favor, asegúrate de iniciar sesión.",
        duration: 6000,
        variant: "default"
      });
      
      return;
    }

    // Intentar obtener el gameId si no está disponible
    let currentGameId = gameId;
    if (!currentGameId) {
      console.log('GameID no disponible, intentando obtenerlo directamente');
      try {
        // Intentar obtener el ID del juego directamente
        const { data, error } = await supabase
          .from('games')
          .select('id')
          .eq('title', 'Memory Cards')
          .single();
        
        if (error) throw error;
        currentGameId = data?.id;
        console.log('ID del juego obtenido directamente:', currentGameId);
        
        // Actualizar la caché de React Query
        if (currentGameId) {
          queryClient.setQueryData(['memory-game-id'], currentGameId);
        }
      } catch (error) {
        console.error('Error obteniendo ID del juego directamente:', error);
      }
    }

    if (!currentGameId) {
      console.error('No se pudo obtener el ID del juego');
      
      // Mostrar resultados sin Cerebrix
      let resultsMessage = `¡Obtuviste ${finalScore} puntos!\n\n`;
      
      // Mostrar desglose de puntos
      bonuses.forEach(bonus => {
        resultsMessage += `${bonus.icon} ${bonus.description}: +${bonus.points}\n`;
      });
      
      toast({
        title: message,
        description: resultsMessage + "\n⚠️ No se pudieron guardar tus Cerebrix. Intenta más tarde.",
        duration: 6000,
        variant: "default"
      });
      
      return;
    }

    // Ahora tenemos tanto la sesión como el gameId, procedemos a registrar la sesión
    console.log('Registrando sesión con:', {
      userId: user.id,
      gameId: currentGameId,
      finalScore
    });
    
    let sessionResult = null;
    let error = null;

    try {
      // Intentar registrar la sesión varias veces si falla
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          console.log('Intentando registrar sesión, intento:', attempt + 1);
          console.log('Parámetros de registro:', {
            p_user_id: user.id,
            p_game_id: currentGameId,
            p_points_earned: finalScore,
            p_session_duration: 60 - gameState.timeLeft,
            p_difficulty_level: gameState.currentLevel,
            p_bonus_multiplier: 1.0
          });
          
          const response = await supabase.rpc('register_game_session', {
            p_user_id: user.id,
            p_game_id: currentGameId,
            p_points_earned: finalScore,
            p_session_duration: 60 - gameState.timeLeft,
            p_difficulty_level: gameState.currentLevel,
            p_bonus_multiplier: 1.0
          });

          console.log('Respuesta de registro:', response);
          console.log('Datos de respuesta:', response.data);

          if (response.error) {
            console.error('Error en la respuesta:', response.error);
            throw response.error;
          }
          
          if (!response.data) {
            console.error('No hay datos en la respuesta');
            throw new Error('No hay datos en la respuesta');
          }
          
          sessionResult = response.data;
          
          // Actualizar el estado con los Cerebrix ganados
          console.log('Resultado de la sesión completo:', sessionResult);
          
          // Asegurarse de que cerebrix_converted sea un número entero
          const cerebrixEarned = sessionResult && typeof sessionResult.cerebrix_converted === 'number' 
            ? sessionResult.cerebrix_converted 
            : 0;
            
          console.log('Cerebrix convertidos (valor original):', sessionResult?.cerebrix_converted);
          console.log('Cerebrix convertidos (tipo):', typeof sessionResult?.cerebrix_converted);
          console.log('Cerebrix convertidos (valor final):', cerebrixEarned);
          
          // Actualizar el estado con el valor correcto de Cerebrix
          setGameState(prev => {
            console.log('Estado anterior:', prev);
            const newState = {
              ...prev,
              cerebrixEarned,
              stats: {
                ...prev.stats,
                points: finalScore
              }
            };
            console.log('Nuevo estado:', newState);
            return newState;
          });
          
          break;
        } catch (e) {
          console.error('Error en intento', attempt + 1, ':', e);
          error = e;
          if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Construir mensaje de resultados
      let resultsMessage = `¡Obtuviste ${finalScore} puntos!\n\n`;
      
      // Mostrar desglose de puntos
      bonuses.forEach(bonus => {
        resultsMessage += `${bonus.icon} ${bonus.description}: +${bonus.points}\n`;
      });

      // Mostrar Cerebrix si se ganaron
      if (sessionResult?.cerebrix_converted) {
        resultsMessage += `\n🧠 ¡Ganaste ${sessionResult.cerebrix_converted} Cerebrix!`;
      } else if (finalScore < GAME_POINTS.MEMORY.MIN_POINTS) {
        resultsMessage += `\n💡 Necesitas ${GAME_POINTS.MEMORY.MIN_POINTS} puntos para ganar Cerebrix`;
      }

      // Mostrar racha si fue notable
      if (gameState.stats.maxStreak > 2) {
        resultsMessage += `\n🔥 ¡Racha máxima de ${gameState.stats.maxStreak} pares!`;
      }

      // Mostrar resultados finales
      toast({
        title: message,
        description: resultsMessage,
        duration: 6000,
        variant: "default"
      });

      // Si se registró exitosamente, actualizar los datos
      if (sessionResult) {
        queryClient.invalidateQueries({ queryKey: ['user-points'] });
        queryClient.invalidateQueries({ queryKey: ['user-cerebrix'] });
        queryClient.invalidateQueries({ queryKey: ['cerebrixStats'] });
        
        // Esperar a que se muestren los resultados antes de reiniciar
        await new Promise(resolve => setTimeout(resolve, 6000));
      }
    } catch (error) {
      console.error('Error al finalizar el juego:', error);
      toast({
        title: "Error",
        description: "Ocurrió un error al guardar tu progreso. Por favor, intenta más tarde.",
        variant: "destructive",
        duration: 4000
      });
    }
  };

  // Verificar fin del juego por tiempo
  useEffect(() => {
    if (!gameState.gameStarted || gameState.isGameOver) return;

    const timer = setInterval(() => {
      setGameState(prev => {
        if (prev.timeLeft <= 0) {
          clearInterval(timer);
          console.log('Tiempo agotado, finalizando juego...');
          // Primero actualizamos el estado
          return {
            ...prev,
            timeLeft: 0,
            isGameOver: true,
            gameStarted: false
          };
        }
        return { ...prev, timeLeft: prev.timeLeft - 1 };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState.gameStarted, gameState.isGameOver]);

  // Verificar fin del juego cuando el tiempo llega a cero
  useEffect(() => {
    if (gameState.timeLeft === 0 && !gameState.gameStarted && gameState.isGameOver) {
      console.log('Ejecutando endGame por tiempo agotado...');
      setTimeout(() => endGame(), 0);
    }
  }, [gameState.timeLeft, gameState.gameStarted, gameState.isGameOver]);

  // Verificar fin del juego cuando se encuentran todos los pares
  useEffect(() => {
    if (gameState.cards.length > 0 && gameState.matchedPairs === gameState.cards.length / 2 && gameState.gameStarted) {
      console.log('Todos los pares encontrados, finalizando juego...');
      setGameState(prev => ({
        ...prev,
        isGameOver: true,
        gameStarted: false
      }));
      setTimeout(() => endGame(), 0);
    }
  }, [gameState.matchedPairs, gameState.cards.length, gameState.gameStarted]);

  return {
    gameState,
    startGame,
    selectCategory,
    handleCardClick,
    endGame
  };
};
